import type { ReactNode } from 'react';
import React, { useContext, useEffect } from 'react';

import { UFOThirdPartySegment } from '@atlaskit/react-ufo/segment';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop.ts';
import {
	EXTENSION_POINT_HOME_FOOTER_EXPERIENCE,
	EXTENSION_POINT_HOME_HEADER_EXPERIENCE,
	EXTENSION_POINT_EDITOR_SAVEBAR_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
	ATL_EDITOR_SAVEBAR,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { filterConnectAppsFromExtensionPoints } from '@confluence/web-item-location/entry-points/webItemUtils.ts';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled.ts';

import type {
	WebPanelLocationQuery as WebPanelLocationQueryType,
	WebPanelLocationQueryVariables as WebPanelLocationQueryVariablesType,
} from './__types__/WebPanelLocationQuery.ts';
import { WebPanel } from './WebPanel.tsx';
import { WebPanelLocationQuery } from './WebPanelLocationQuery.graphql.ts';

export type WebPanel = {
	completeKey: string;
	html: string;
	weight: number;
	label: string;
};

type WebPanelLocationProps = {
	location: string;
	style?: { [key: string]: string };
	// Please note it is default to "cache-and-network".
	// You can safely put it to "cache-first" only when the data don't change during the entire browser session.
	fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
	onCompleted?: () => void;
	children?: (props: { panels: WebPanel[] }) => ReactNode;
	renderWhenLoading?: boolean;
};

const UFOWebPanelLocationWrapper = ({
	children,
	locationName,
	panels,
}: {
	children: ReactNode;
	locationName: string;
	panels: WebPanel[];
}) => {
	if (!fg('confluence_annotate_3p_web_panel')) {
		return children;
	}

	// If all panels are empty, exclude it from UFOThirdPartySegment
	const panelHasContent = panels?.length && panels.some((panel) => panel.html !== '');
	if (!panelHasContent) {
		return children;
	}

	return <UFOThirdPartySegment name={`web-panel-${locationName}`}>{children}</UFOThirdPartySegment>;
};

export const WebPanelLocation = ({
	location,
	style = {},
	renderWhenLoading,
	// Query is preloaded on the server-side. reused the preloaded data.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	fetchPolicy = process.env.REACT_SSR ? 'cache-first' : 'cache-and-network',
	children,
	onCompleted,
}: WebPanelLocationProps) => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();

	const EXPERIENCE_NAME_BY_LOCATION = {
		[ATL_FOOTER]: EXTENSION_POINT_HOME_FOOTER_EXPERIENCE,
		[ATL_GENERAL]: EXTENSION_POINT_HOME_HEADER_EXPERIENCE,
		[ATL_EDITOR_SAVEBAR]: EXTENSION_POINT_EDITOR_SAVEBAR_EXPERIENCE,
	};

	const experienceName = EXPERIENCE_NAME_BY_LOCATION[location] || null;

	useEffect(() => {
		if (experienceName) {
			experienceTracker.start({ name: experienceName });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const spaceIdentifier =
		spaceKey || getSingleParam(queryParams, 'spaceKey') || getSingleParam(queryParams, 'key');
	const contentIdentifier = contentId || getSingleParam(queryParams, 'pageId');

	// TODO: The task to remove this hack is captured in CFE-1873.
	// There's some weird scenario where the UI ends up with a space key of "byDraft" relating to the way tinymce
	// creates content. Given that it's tinymce specific, I'm applying this hack for now and we can remove it once we're
	// at 100% rollout for the Fabric editor.
	if (spaceIdentifier === 'byDraft') return null;

	const variables = contentIdentifier
		? { contentId: contentIdentifier, location }
		: { spaceKey: spaceIdentifier, location };

	return (
		<div
			/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
			style={style}
			data-vc-ignore-if-no-layout-shift
		>
			<Query<WebPanelLocationQueryType, WebPanelLocationQueryVariablesType>
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query={WebPanelLocationQuery}
				variables={variables}
				fetchPolicy={fetchPolicy}
				onCompleted={onCompleted}
			>
				{({ data, loading, error }) => {
					// For transitions, loading and data is true
					if (loading && data) {
						return null;
					}

					if (!renderWhenLoading) {
						if (loading || error || !data) {
							return null;
						}
					}

					let panels = (data?.webPanels || []).filter((panel) => panel) as WebPanel[];

					if (isLivePagesFeatureEnabled) {
						panels = filterConnectAppsFromExtensionPoints(location, panels);
					}

					// For panels that don't have a placeholder
					if (!renderWhenLoading && !panels.length) {
						if (experienceName) {
							experienceTracker.succeed({
								name: experienceName,
							});
						}
						return null;
					}

					return (
						<>
							<UFOWebPanelLocationWrapper locationName={location} panels={panels}>
								{children
									? children({ panels })
									: panels.map((panel) => {
											return (
												<WebPanel
													key={panel.completeKey}
													panelId={panel.completeKey}
													html={panel.html}
												/>
											);
										})}
							</UFOWebPanelLocationWrapper>
							{experienceName ? <ExperienceSuccess name={experienceName} /> : null}
						</>
					);
				}}
			</Query>
		</div>
	);
};
